import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { NtIcon, Pill, STATUS_COLORS, PRIORITY_COLORS } from '../components/NotionUI';
import DynamicFieldInput from '../components/DynamicFieldInput';
import AssigneePicker from '../components/AssigneePicker';
import { userColor } from '../utils/userColor';
import TicketDrafts from '../components/TicketDrafts';
import SimilarTickets from '../components/SimilarTickets';
import { Stars } from '../components/CsatPanel';
import CollaboratorsPicker from '../components/CollaboratorsPicker';
import TicketProjectTasks from '../components/TicketProjectTasks';
import {
  getTicket,
  updateTicket,
  updateTicketStatus,
  addTicketComment,
  createTicketDraft,
  getCannedResponses,
  uploadTicketAttachment,
  downloadTicketAttachment,
  fetchTicketAttachmentBlob,
  getUsers,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { fmtDateTime, fmtRelativeDate } from '../utils/format';
import { dueDateStatus } from '../utils/ticketDue';
import Select from '../components/Select';

const STATUSES = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

function FileIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function initialsFromNameOrEmail(name, email) {
  const source = (name || '').trim();
  if (source) {
    const parts = source.split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return '?';
}

function TicketDetail({ user, onLogout }) {
  const { t, lang } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Chi arriva da un'altra pagina (progetto, clienti, dashboard, casella email) torna lì; altrimenti all'elenco ticket.
  const backTo = typeof location.state?.from === 'string' && location.state.from.startsWith('/') ? location.state.from : '/tickets';
  const backKey = backTo.startsWith('/projects/') ? 'backToProject'
    : backTo.startsWith('/customers') ? 'backToCustomers'
      : backTo.startsWith('/dashboard') ? 'backToDashboard'
        : backTo.startsWith('/ticket-projects/') ? 'backToMailbox'
          : backTo === '/tickets' ? 'backToList' : 'backGeneric';
  const fileInputRef = useRef(null);

  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [canned, setCanned] = useState([]);
  const [rating, setRating] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [fieldValues, setFieldValues] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [previewUrls, setPreviewUrls] = useState({});
  const previewUrlsRef = useRef({});

  const fetchTicket = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getTicket(id);
      setTicket(data.ticket);
      setComments(data.comments || []);
      setDrafts(data.drafts || []);
      setRating(data.rating || null);
      setAttachments(data.attachments || []);
      setFieldValues(data.field_values || []);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Risposte pronte della coda del ticket (e quelle generiche), da inserire nel campo di risposta.
  const ticketProjectId = ticket?.project_id;
  useEffect(() => {
    if (!ticketProjectId) return;
    getCannedResponses(ticketProjectId).then((d) => setCanned(d.responses || [])).catch(() => setCanned([]));
  }, [ticketProjectId]);

  // "@Nome Cognome" nel testo avvisa il collega con una notifica.
  const insertMention = (agentId) => {
    const agent = agents.find((a) => String(a.id) === String(agentId));
    if (!agent) return;
    const mention = `@${[agent.name, agent.surname].filter(Boolean).join(' ')} `;
    setReplyBody((prev) => (prev && !/\s$/.test(prev) ? `${prev} ${mention}` : `${prev}${mention}`));
  };

  const insertCanned = (cannedId) => {
    const item = canned.find((c) => String(c.id) === String(cannedId));
    if (!item) return;
    const values = {
      customer_name: ticket.customer_name || ticket.customer_email || '',
      ticket_id: String(ticket.id),
      subject: ticket.subject || '',
      agent_name: [user.name, user.surname].filter(Boolean).join(' '),
    };
    const text = item.body.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => (key in values ? values[key] : m));
    setIsInternal(false);
    setReplyBody((prev) => (prev.trim() ? `${prev.replace(/\s+$/, '')}\n\n${text}` : text));
  };

  useEffect(() => {
    fetchTicket();
    getUsers().then((d) => setAgents(d.users || [])).catch(() => {});
  }, [fetchTicket]);

  // Le anteprime immagine richiedono il Bearer token per essere scaricate
  // (vedi fetchTicketAttachmentBlob), quindi non si può usare direttamente
  // l'URL dell'allegato in un <img src> - si scaricano qui come blob e si
  // tiene un object URL locale per ciascuna, revocato quando non serve più.
  useEffect(() => {
    let active = true;
    const imageAttachments = attachments.filter((a) => (a.mime_type || '').startsWith('image/'));
    (async () => {
      const entries = await Promise.all(imageAttachments.map(async (a) => {
        try {
          const blob = await fetchTicketAttachmentBlob(ticket?.id, a.id);
          return [a.id, window.URL.createObjectURL(blob)];
        } catch {
          return null;
        }
      }));
      if (!active) return;
      const next = Object.fromEntries(entries.filter(Boolean));
      Object.entries(previewUrlsRef.current).forEach(([attId, url]) => {
        if (next[attId] !== url) window.URL.revokeObjectURL(url);
      });
      previewUrlsRef.current = next;
      setPreviewUrls(next);
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments, ticket?.id]);

  useEffect(() => () => {
    Object.values(previewUrlsRef.current).forEach((url) => window.URL.revokeObjectURL(url));
  }, []);

  const handleOpenAttachment = (a) => {
    const cached = previewUrlsRef.current[a.id];
    if (cached) {
      window.open(cached, '_blank', 'noopener');
      return;
    }
    // L'allegato va scaricato (richiede il Bearer token, vedi
    // fetchTicketAttachmentBlob) prima di poterlo aprire, ma un window.open
    // chiamato DOPO un await perde lo "user activation" del click e i
    // browser lo bloccano come popup - si apre quindi subito una scheda
    // vuota, nello stesso gesto sincrono del click, e la si naviga verso
    // l'object URL una volta pronto.
    const win = window.open('', '_blank');
    fetchTicketAttachmentBlob(ticket.id, a.id)
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        if (win) {
          win.location.href = url;
        } else {
          window.open(url, '_blank', 'noopener');
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      })
      .catch((err) => {
        if (win) win.close();
        setError(errorMessage(err));
      });
  };

  const handleFieldChange = async (field, value) => {
    setTicket((prev) => ({
      ...prev,
      [field]: value,
      // chi diventa responsabile esce subito dai collaboratori (il server conferma subito dopo)
      ...(field === 'assignee_id' ? { collaborators: (prev.collaborators || []).filter((c) => c.id !== value) } : {}),
    }));
    try {
      if (field === 'status') {
        await updateTicketStatus(id, value);
      } else {
        const res = await updateTicket(id, { [field]: value || null });
        // Chi diventa responsabile esce dai collaboratori: allineo l'elenco a quello del server.
        if (field === 'assignee_id' && res?.ticket) {
          setTicket((prev) => ({ ...prev, collaborators: res.ticket.collaborators || [] }));
        }
      }
    } catch (err) {
      setError(errorMessage(err));
      fetchTicket();
    }
  };

  const handleCollaboratorsChange = async (ids) => {
    const chosen = ids.map((cid) => agents.find((a) => a.id === cid)).filter(Boolean)
      .map((a) => ({ id: a.id, name: a.name, surname: a.surname || '' }));
    setTicket((prev) => ({ ...prev, collaborators: chosen }));
    try {
      await updateTicket(id, { collaborator_ids: ids });
    } catch (err) {
      setError(errorMessage(err));
      fetchTicket();
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim()) return;
    setSending(true);
    setError('');
    try {
      const res = await addTicketComment(id, replyBody.trim(), isInternal);
      setComments(res.comments || []);
      setReplyBody('');
      setIsInternal(false);
    } catch (err) {
      setError(errorMessage(err, t('tickets.commentError')));
    } finally {
      setSending(false);
    }
  };

  // "Salva come bozza": il testo resta nel ticket, da rivedere/inviare più tardi (anche da un collega).
  const handleSaveDraft = async () => {
    if (!replyBody.trim()) return;
    setSending(true);
    setError('');
    try {
      const res = await createTicketDraft(id, replyBody.trim());
      setDrafts((prev) => [...prev, res.draft]);
      setReplyBody('');
    } catch (err) {
      setError(errorMessage(err, t('tickets.draftError')));
    } finally {
      setSending(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setUploadCount(files.length);
    setError('');
    try {
      const res = await uploadTicketAttachment(id, files);
      setAttachments(res.attachments || []);
    } catch (err) {
      setError(errorMessage(err, t('tickets.uploadError')));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="loading">{t('common.loading')}</div>
      </Layout>
    );
  }

  if (!ticket) {
    return (
      <Layout user={user} onLogout={onLogout}>
        {error && <div className="error-message">{error}</div>}
        <button className="btn btn-secondary" type="button" onClick={() => navigate(backTo)}>
          {t(`tickets.${backKey}`)}
        </button>
      </Layout>
    );
  }

  const dueStatus = dueDateStatus(ticket);
  const requesterLabel = ticket.customer_email || ticket.customer_name || t('tickets.requesterStaff');
  const requesterInitials = initialsFromNameOrEmail(ticket.customer_name, ticket.customer_email);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page td-page">
      <button className="tpl-back fd-back" type="button" onClick={() => navigate(backTo)}>
        <NtIcon name="back" /> {t(`tickets.${backKey}`)}
      </button>

      <header className="td-head" style={{ '--q': ticket.project_color || 'var(--border)' }}>
        <div className="td-head-top">
          <span className="td-id">#{ticket.id}</span>
          <Pill color={ticket.project_color}>{ticket.project_name}</Pill>
          <Pill dot color={STATUS_COLORS[ticket.status]}>{t(`tickets.status_${ticket.status}`)}</Pill>
          <span className="tk-prio" style={{ '--c': PRIORITY_COLORS[ticket.priority] }}>
            <i aria-hidden="true" /> {t(`tickets.priority_${ticket.priority}`)}
          </span>
          {ticket.due_at && dueStatus !== 'none' && (
            <span className={`tk-due tk-due-${dueStatus}`}>
              <NtIcon name="clock" size={12} /> {fmtRelativeDate(ticket.due_at)}
              {dueStatus === 'overdue' && ` · ${t('tickets.overdue')}`}
            </span>
          )}
        </div>
        <h1 className="td-title">{ticket.subject}</h1>
        <div className="td-head-meta">
          <span className="avatar avatar-sm avatar-empty" aria-hidden="true">{requesterInitials}</span>
          <span>{requesterLabel}</span>
          {ticket.group_name && (
            <button
              type="button"
              className="cg-tag cg-tag-link"
              style={{ '--pill': ticket.group_color }}
              title={t('tickets.customerGroupHint')}
              onClick={() => navigate(`/tickets?group=${ticket.customer_group_id}`)}
            >
              <i className="cg-dot" aria-hidden="true" />{ticket.group_name}
            </button>
          )}
          <span aria-hidden="true">·</span>
          <span>{t('tickets.createdAt')} {fmtDateTime(ticket.created_at)}</span>
        </div>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="ticket-detail-layout td-layout">
        <div className="ticket-detail-main td-main">
          <section className="td-card">
            <h3 className="td-card-title">{t('tickets.formDescription')}</h3>
            <p className="td-description">{ticket.description || t('tickets.noDescription')}</p>
          </section>

          {fieldValues.length > 0 && (
            <section className="td-card">
              <h3 className="td-card-title">{t('tickets.additionalFields')}</h3>
              <div className="form-grid td-fields">
                {fieldValues.map((fv) => (
                  <DynamicFieldInput
                    key={fv.field_id}
                    field={fv}
                    value={fv.value}
                    locale={lang}
                    readOnly
                  />
                ))}
              </div>
            </section>
          )}

          <section className="td-card">
            <div className="td-card-head">
              <h3 className="td-card-title">{t('tickets.attachments')}{attachments.length > 0 ? ` (${attachments.length})` : ''}</h3>
              {uploading ? (
                <div className="upload-status">
                  <span className="spinner" aria-hidden="true" />
                  {t('tickets.uploadingCount', { count: uploadCount })}
                </div>
              ) : (
                <label className="btn btn-secondary btn-sm upload-trigger">
                  + {t('tickets.addAttachment')}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleUpload}
                    className="upload-trigger-input"
                  />
                </label>
              )}
            </div>
            {attachments.length === 0 ? (
              <p className="td-muted">{t('tickets.noAttachments')}</p>
            ) : (
              <div className="td-files">
                {attachments.map((a) => {
                  const isImage = (a.mime_type || '').startsWith('image/');
                  return (
                    <div key={a.id} className="td-file">
                      <button
                        type="button"
                        className="td-file-thumb"
                        onClick={() => handleOpenAttachment(a)}
                        title={a.original_filename}
                      >
                        {isImage && previewUrls[a.id] ? (
                          <img src={previewUrls[a.id]} alt={a.original_filename} />
                        ) : (
                          <span className="td-file-icon"><FileIcon /></span>
                        )}
                      </button>
                      <div className="td-file-info">
                        <span className="td-file-name" title={a.original_filename}>{a.original_filename}</span>
                        <button
                          type="button"
                          className="td-file-dl"
                          onClick={() => downloadTicketAttachment(ticket.id, a.id, a.original_filename)}
                        >
                          ↓ {t('tickets.download')}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="td-card">
            <h3 className="td-card-title">{t('tickets.thread')}{comments.length > 0 ? ` (${comments.length})` : ''}</h3>
            <div className="td-thread">
              {comments.map((c) => {
                const authorName = c.author_type === 'agent'
                  ? [c.agent_name, c.agent_surname].filter(Boolean).join(' ')
                  : c.author_type === 'automation'
                    ? (c.automation_name || t('automations.authorFallback'))
                    : (c.customer_name || t('tickets.requesterStaff'));
                const kind = c.author_type === 'automation' ? 'automation' : c.author_type === 'agent' ? 'agent' : 'customer';
                return (
                  <div key={c.id} className={`td-msg td-msg-${kind}${c.is_internal_note ? ' td-msg-internal' : ''}`}>
                    <span
                      className={
                        'avatar avatar-sm' +
                        (kind === 'agent' ? '' : kind === 'automation' ? ' avatar-automation' : ' avatar-empty')
                      }
                      style={kind === 'agent' && c.author_user_id ? { background: userColor({ id: c.author_user_id }) } : undefined}
                    >
                      {kind === 'automation' ? '🤖' : initialsFromNameOrEmail(authorName)}
                    </span>
                    <div className="td-bubble">
                      <div className="td-bubble-head">
                        <strong>{authorName}</strong>
                        <span className="td-muted">{fmtDateTime(c.created_at)}</span>
                        {c.is_internal_note ? (
                          <span className="td-tag td-tag-note">{t('tickets.internalNoteBadge')}</span>
                        ) : (
                          <span className="td-tag">{t('tickets.publicReplyBadge')}</span>
                        )}
                      </div>
                      <div className="td-bubble-body">{c.body}</div>
                    </div>
                  </div>
                );
              })}
              {comments.length === 0 && <p className="td-muted">{t('tickets.noMessages')}</p>}
            </div>

            <TicketDrafts
              drafts={drafts}
              onChanged={fetchTicket}
              onError={setError}
              onSent={(res) => {
                setComments(res.comments || []);
                setDrafts(res.drafts || []);
              }}
            />

            <form onSubmit={handleReply} className={'td-composer' + (isInternal ? ' td-composer-note' : '')}>
              <div className="td-composer-tabs" role="tablist">
                <button type="button" role="tab" aria-selected={!isInternal} className={!isInternal ? 'td-ctab td-ctab-on' : 'td-ctab'} onClick={() => setIsInternal(false)}>
                  <NtIcon name="mail" /> {t('tickets.publicReplyBadge')}
                </button>
                <button type="button" role="tab" aria-selected={isInternal} className={isInternal ? 'td-ctab td-ctab-on td-ctab-note' : 'td-ctab'} onClick={() => setIsInternal(true)}>
                  <NtIcon name="edit" /> {t('tickets.internalNote')}
                </button>
              </div>
              <textarea
                rows={4}
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder={isInternal ? t('tickets.noteHint') : t('tickets.replyPlaceholder')}
              />
              {agents.length > 0 && (
                <div className="td-canned">
                  <Select value="" onChange={(e) => insertMention(e.target.value)} aria-label={t('tickets.mentionInsert')}>
                    <option value="">{t('tickets.mentionInsert')}</option>
                    {agents.filter((a) => a.id !== user.id).map((a) => (
                      <option key={a.id} value={a.id}>{[a.name, a.surname].filter(Boolean).join(' ')}</option>
                    ))}
                  </Select>
                </div>
              )}
              {!isInternal && canned.length > 0 && (
                <div className="td-canned">
                  <Select value="" onChange={(e) => insertCanned(e.target.value)} aria-label={t('tickets.cannedInsert')}>
                    <option value="">{t('tickets.cannedInsert')}</option>
                    {canned.map((c) => (
                      <option key={c.id} value={c.id}>{c.title}</option>
                    ))}
                  </Select>
                </div>
              )}
              <div className="td-composer-foot">
                <span className="td-muted">{isInternal ? t('tickets.internalNoteCheckbox') : t('tickets.publicHint')}</span>
                {!isInternal && (
                  <button type="button" className="btn btn-secondary" disabled={sending || !replyBody.trim()} onClick={handleSaveDraft}>
                    {t('tickets.draftSaveAs')}
                  </button>
                )}
                <button type="submit" className="btn" disabled={sending || !replyBody.trim()}>
                  {sending ? t('tickets.sending') : t('tickets.send')}
                </button>
              </div>
            </form>
          </section>
        </div>

        <div className="ticket-detail-sidebar td-side">
          <section className="td-card td-props">
            <div className="td-prop">
              <label className="sidebar-field-label">{t('tickets.status')}</label>
              <Select
                className="dd-pill-round"
                pill
                value={ticket.status}
                aria-label={t('tickets.status')}
                onChange={(e) => handleFieldChange('status', e.target.value)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s} data-color={STATUS_COLORS[s]}>{t(`tickets.status_${s}`)}</option>
                ))}
              </Select>
            </div>

            <div className="td-prop">
              <label className="sidebar-field-label">{t('tickets.priority')}</label>
              <Select
                className="dd-pill-small"
                pill
                value={ticket.priority}
                aria-label={t('tickets.priority')}
                onChange={(e) => handleFieldChange('priority', e.target.value)}
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p} data-color={PRIORITY_COLORS[p]}>{t(`tickets.priority_${p}`)}</option>
                ))}
              </Select>
            </div>

            <div className="td-prop">
              <label className="sidebar-field-label">{t('tickets.assignee')}</label>
              <AssigneePicker
                value={ticket.assignee_id}
                agents={agents}
                me={user.id}
                onChange={(assigneeId) => handleFieldChange('assignee_id', assigneeId)}
              />
              {ticket.assignee_id !== user.id && (
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => handleFieldChange('assignee_id', user.id)}
                >
                  {t('tickets.assignToMe')}
                </button>
              )}
            </div>

            <div className="td-prop">
              <label className="sidebar-field-label">{t('tickets.collaborators')}</label>
              <CollaboratorsPicker
                me={user.id}
                collaborators={ticket.collaborators || []}
                agents={agents}
                primaryId={ticket.assignee_id}
                onChange={handleCollaboratorsChange}
              />
            </div>

            <div className="td-prop">
              <label className="sidebar-field-label">{t('tickets.dueAt')}</label>
              <div className={`due-date-field due-date-field-${dueStatus}`}>
                <span className="due-date-icon"><NtIcon name="calendar" size={15} /></span>
                <input
                  type="date"
                  className="due-date-input"
                  value={ticket.due_at ? ticket.due_at.slice(0, 10) : ''}
                  onChange={(e) => handleFieldChange('due_at', e.target.value)}
                />
              </div>
              {ticket.due_at && (
                <p className={`due-date-caption due-date-caption-${dueStatus}`}>
                  {fmtRelativeDate(ticket.due_at)}
                  {dueStatus === 'overdue' && ` · ${t('tickets.overdue')}`}
                  {dueStatus === 'warning' && ` · ${t('tickets.dueSoon')}`}
                </p>
              )}
            </div>

            {rating && (
              <div className="td-prop">
                <label className="sidebar-field-label">{t('tickets.customerRating')}</label>
                <Stars value={rating.rating} size={18} />
                {rating.comment && <p className="td-rating-comment">“{rating.comment}”</p>}
              </div>
            )}

            <div className="td-prop">
              <label className="sidebar-field-label">{t('tickets.requester')}</label>
              <div className="sidebar-field-value">
                <span className="avatar avatar-sm avatar-empty">{requesterInitials}</span>
                <span className="td-requester">{requesterLabel}</span>
              </div>
            </div>
          </section>

          <SimilarTickets text={`${ticket.subject} ${ticket.description || ''}`} excludeId={ticket.id} from={`/tickets/${ticket.id}`} />

          <TicketProjectTasks ticket={ticket} user={user} />
        </div>
      </div>
      </div>
    </Layout>
  );
}

export default TicketDetail;
